import { useState } from 'react';
import { ServiceBooking, ServiceOrder } from '@turizi/shared';
import { Link } from 'react-router-dom';
import { EDIT_SERVICE_ORDER_PATH } from '@/lib/navigation';
import { IconFileText, IconExternalLink } from '@tabler/icons-react';
import { Popover, Loader } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useServiceOrder } from '@/hooks/api-hooks';
import ServiceOrderSummaryDetail from '../service-orders/service-order-summary-detail';
import AddBookingToOrderPopover from '../service-orders/add-booking-to-order-popover';
import { BookingPaxsSize } from '../snippets';
import ServiceOrderBookingsSummary from '../bookings-summary';

interface ServiceOrderPopoverItemProps {
    orderId: number;
    badgeColor: string;
    orderUrl: string;
    readOnly?: boolean;
    paxInfo?: {
        selected: number;
        total: number;
    };
}

function ServiceOrderPopoverItem({
    orderId,
    badgeColor,
    orderUrl,
    readOnly = false,
    paxInfo,
}: ServiceOrderPopoverItemProps) {
    const { t } = useTranslation();
    const [opened, setOpened] = useState(false);

    // Fetch the full ServiceOrder details dynamically only when the Popover is opened
    // and if we don't already have the initial populated service order.
    const {
        data: orderRes,
        isLoading
    } = useServiceOrder(orderId, {
        enabled: opened
    });

    const serviceOrder = orderRes?.data;

    return (
        <Popover
            opened={opened}
            onChange={setOpened}
            position="bottom-end"
            withArrow
            shadow="md"
            // width={420}
            withinPortal
        >
            <Popover.Target>
                <div
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setOpened((o) => !o);
                    }}
                    className="flex items-center gap-0.5 text-xs font-bold whitespace-nowrap cursor-pointer hover:bg-gray-50 rounded"
                    style={{
                        color: badgeColor,
                    }}
                    title={
                        badgeColor === 'orange' && paxInfo
                            ? t('paxs_selected_unselected_tooltip', {
                                selected: paxInfo.selected,
                                unassigned: paxInfo.total - paxInfo.selected,
                            })
                            : undefined
                    }
                >
                    <IconFileText
                        size={16}
                        color={badgeColor}
                        stroke={2.5}
                    />
                    <span>{orderId}</span>
                    {badgeColor === 'orange' && paxInfo && (
                        <span className="ml-0.5 text-[10px] opacity-80">
                            ({paxInfo.selected}/{paxInfo.total})
                        </span>
                    )}
                </div>
            </Popover.Target>
            <Popover.Dropdown p="xs" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-2">
                    <Link
                        target='_blank'
                        to={orderUrl}
                        className="text-xs font-bold text-blue-600 hover:underline flex items-center gap-1"
                        onClick={(e) => {
                            e.stopPropagation();
                            setOpened(false);
                        }}
                    >
                        <IconFileText size={12} />{orderId}
                        <IconExternalLink size={9} stroke={1} />
                    </Link>
                </div>
                {isLoading && <Loader size="xs" color='black' />}
                {serviceOrder && (
                    <>
                        <ServiceOrderSummaryDetail
                            order={serviceOrder}
                            readOnly={readOnly}
                        />
                        <ServiceOrderBookingsSummary
                            className='solid-box-border-positive text-xs mt-2 space-y-2'
                            serviceOrder={serviceOrder}
                            editable={!readOnly}
                        />
                    </>
                )}
            </Popover.Dropdown>
        </Popover>
    );
}

interface BookingServiceOrdersProps {
    booking: ServiceBooking;
    editable?: boolean;
}

export default function BookingServiceOrders(
    {
        booking,
        editable = true
    }: BookingServiceOrdersProps) {
    const totalBookingPaxs = BookingPaxsSize(booking);

    if (totalBookingPaxs === 0) {
        return null;
    }

    const orderBookings = booking.service_order_bookings || [];

    const allOrdersSum = orderBookings.reduce((acc, sob) => {
        return acc + BookingPaxsSize(sob);
    }, 0);

    return (
        <div className="flex gap-1">
            {orderBookings.map((sob) => {
                const orderUrl = EDIT_SERVICE_ORDER_PATH.replace(':id', sob.service_order_id.toString())
                let badgeColor = 'orange';
                if (BookingPaxsSize(sob) === totalBookingPaxs) {
                    badgeColor = 'green';
                }
                return (
                    <div key={sob.id} className="solid-box-border-hover flex items-center gap-1">
                        <ServiceOrderPopoverItem
                            orderId={sob.service_order_id}
                            badgeColor={badgeColor}
                            orderUrl={orderUrl}
                            readOnly={!editable}
                            paxInfo={{
                                selected: BookingPaxsSize(sob),
                                total: totalBookingPaxs,
                            }}
                        />
                    </div>
                );
            })}

            {/* show add order ONLY if needed */}
            {editable && (allOrdersSum !== totalBookingPaxs) && (
                <AddBookingToOrderPopover serviceBookingId={booking.id} />
            )}
        </div>
    );
}
